import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import {
  DateRangeFormComponent,
  NgDialogModule,
} from '@principle-theorem/ng-shared';
import { StatisticComponent } from '../../statistic/statistic.component';
import {
  CampaignCostForecast,
  type ICampaignCostForecast,
} from '@principle-theorem/principle-core';
import { type IMarketingCampaign } from '@principle-theorem/principle-core/interfaces';
import {
  type ITimePeriod,
  toTimestamp,
  type WithRef,
} from '@principle-theorem/shared';
import * as moment from 'moment-timezone';
import { EMPTY, Subject, from } from 'rxjs';
import { catchError, debounceTime, switchMap, tap } from 'rxjs/operators';

export interface ICampaignCostForecastDialogRequest {
  campaign: WithRef<IMarketingCampaign>;
}

@Component({
  selector: 'pr-campaign-cost-forecast-dialog',
  templateUrl: './campaign-cost-forecast-dialog.component.html',
  styleUrl: './campaign-cost-forecast-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgDialogModule,
    DateRangeFormComponent,
    StatisticComponent,
    MatProgressSpinnerModule,
  ],
})
export class CampaignCostForecastDialogComponent {
  private _dialogRef = inject(
    MatDialogRef<CampaignCostForecastDialogComponent, void>
  );
  private _destroyRef = inject(DestroyRef);
  private _forecastRequest$ = new Subject<ITimePeriod>();

  data = inject<ICampaignCostForecastDialogRequest>(MAT_DIALOG_DATA);
  forecast = signal<ICampaignCostForecast | undefined>(undefined);
  loading = signal(false);
  defaultRange: ITimePeriod = {
    from: moment(),
    to: moment().add(7, 'days'),
  };

  constructor() {
    this._forecastRequest$
      .pipe(
        debounceTime(300),
        tap(() => {
          this.loading.set(true);
          this.forecast.set(undefined);
        }),
        switchMap((dateRange) => {
          const windowStart = toTimestamp(dateRange.from);
          const windowEnd = toTimestamp(dateRange.to);
          return from(
            CampaignCostForecast.forecastCosts(
              this.data.campaign,
              windowStart,
              windowEnd
            )
          ).pipe(
            catchError(() => {
              this.loading.set(false);
              return EMPTY;
            })
          );
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe((result) => {
        this.forecast.set(result);
        this.loading.set(false);
      });
  }

  runForecast(dateRange: ITimePeriod): void {
    this._forecastRequest$.next(dateRange);
  }

  close(): void {
    this._dialogRef.close();
  }
}
